import React from 'react';

interface LogoProps {
    size?: 'sm' | 'md' | 'lg' | 'xl';
    showTagline?: boolean;
    className?: string;
    onClick?: () => void;
}

export const Logo: React.FC<LogoProps> = ({
    size = 'md',
    showTagline = false,
    className = '',
    onClick
}) => {
    const sizeConfig = {
        sm: {
            title: 'text-xl sm:text-2xl',
            star: 'w-3.5 h-3.5',
            tagline: 'text-[9px]',
            gap: 'gap-1'
        },
        md: {
            title: 'text-2xl sm:text-3xl',
            star: 'w-4 h-4',
            tagline: 'text-[10px] sm:text-xs',
            gap: 'gap-1'
        },
        lg: {
            title: 'text-3xl sm:text-4xl lg:text-5xl',
            star: 'w-5 h-5 lg:w-6 lg:h-6',
            tagline: 'text-xs sm:text-sm',
            gap: 'gap-1.5'
        },
        xl: {
            title: 'text-4xl sm:text-5xl lg:text-6xl',
            star: 'w-6 h-6 lg:w-8 lg:h-8',
            tagline: 'text-sm sm:text-base',
            gap: 'gap-2'
        }
    }[size];

    return (
        <div 
            onClick={onClick}
            className={`inline-flex flex-col items-center select-none group cursor-pointer ${className}`}
        >
            <div className="relative flex items-center leading-none">
                {/* Celestial Golden Arc Glow */}
                <svg 
                    className="absolute -top-2.5 -left-1.5 w-[112%] h-4 pointer-events-none opacity-80 group-hover:opacity-100 transition-opacity"
                    viewBox="0 0 100 20" 
                    fill="none"
                >
                    <path 
                        d="M 5,18 Q 50,0 95,18" 
                        stroke="url(#goldArcGradient)" 
                        strokeWidth="1.2" 
                        strokeLinecap="round"
                    />
                    <defs>
                        <linearGradient id="goldArcGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.1" />
                            <stop offset="50%" stopColor="#F59E0B" stopOpacity="0.9" />
                            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.1" />
                        </linearGradient>
                    </defs>
                </svg>

                {/* Main Logo Text */}
                <span className={`font-serif font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-amber-100 via-yellow-200 to-amber-300 drop-shadow-[0_2px_12px_rgba(245,158,11,0.45)] ${sizeConfig.title}`}>
                    Astrox<span className="text-amber-400 font-sans font-medium">.fr</span>
                </span>

                {/* 8-Point Cosmic Gold Star */}
                <span className="absolute -top-1.5 right-4 sm:right-5 pointer-events-none animate-twinkle">
                    <svg className={`${sizeConfig.star} text-amber-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.9)]`} viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z" />
                    </svg>
                </span>
            </div>

            {/* Sub-tagline */}
            {showTagline && (
                <div className={`font-serif tracking-widest text-purple-200/90 font-medium uppercase mt-1 flex items-center gap-1.5 ${sizeConfig.tagline}`}>
                    <span>Voyance</span>
                    <span className="text-amber-400">•</span>
                    <span>Guidance</span>
                    <span className="text-amber-400">•</span>
                    <span>Évolution</span>
                </div>
            )}
        </div>
    );
};

export default Logo;
